import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Calendar, MapPin, Users, ChevronRight, ClipboardCheck, List, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import MiniCalendar from "@/components/formateur/MiniCalendar";

const FILTERS = [
    { key: "upcoming", label: "À venir" },
    { key: "past", label: "Passées" },
    { key: "all", label: "Toutes" },
];

function formatDate(iso) {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
}

export default function FormateurSessions() {
    const [filter, setFilter] = useState("upcoming");
    const [view, setView] = useState("list"); // "list" | "calendar"
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        setLoading(true);
        // Calendar view always pulls all sessions to give context across months
        const when = view === "calendar" ? "all" : filter;
        api.get(`/formateur/sessions?when=${when}`)
            .then((r) => setItems(r.data))
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    }, [filter, view]);

    return (
        <div className="mx-auto max-w-6xl px-6 py-10" data-testid="page-formateur-sessions">
            <div className="flex items-start justify-between gap-6">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-orange">Mes interventions</div>
                    <h1 className="mt-2 font-display text-5xl font-normal tracking-tightest">Mes sessions</h1>
                    <p className="mt-3 max-w-2xl text-brand-smoke">
                        Toutes les sessions où vous êtes assigné(e) comme formateur. Validation des émargements et bilan
                        pédagogique disponibles depuis le détail.
                    </p>
                </div>
                <div className="hidden h-16 w-16 items-center justify-center rounded-2xl bg-brand-orange/10 text-brand-orange md:flex">
                    <Calendar className="h-7 w-7" />
                </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
                <div className="inline-flex rounded-full border border-brand-line bg-white p-1">
                    {FILTERS.map((f) => (
                        <button
                            key={f.key}
                            onClick={() => setFilter(f.key)}
                            data-testid={`formateur-sessions-filter-${f.key}`}
                            disabled={view === "calendar"}
                            className={`rounded-full px-4 py-1.5 text-sm font-medium transition disabled:opacity-50 ${
                                filter === f.key
                                    ? "bg-brand-orange text-white"
                                    : "text-brand-smoke hover:text-brand-ink"
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
                <div className="inline-flex rounded-full border border-brand-line bg-white p-1">
                    <button
                        onClick={() => setView("list")}
                        data-testid="formateur-view-list"
                        className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition ${
                            view === "list" ? "bg-brand-orange text-white" : "text-brand-smoke hover:text-brand-ink"
                        }`}
                    >
                        <List className="h-3.5 w-3.5" /> Liste
                    </button>
                    <button
                        onClick={() => setView("calendar")}
                        data-testid="formateur-view-calendar"
                        className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition ${
                            view === "calendar" ? "bg-brand-orange text-white" : "text-brand-smoke hover:text-brand-ink"
                        }`}
                    >
                        <LayoutGrid className="h-3.5 w-3.5" /> Calendrier
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="mt-10 text-center text-brand-smoke">Chargement…</div>
            ) : view === "calendar" ? (
                <div className="mt-8">
                    <MiniCalendar
                        sessions={items}
                        onSessionClick={(s) => navigate(`/formateur/sessions/${s.id}`)}
                    />
                </div>
            ) : items.length === 0 ? (
                <div className="mt-10 rounded-2xl border border-dashed border-brand-line bg-white p-10 text-center text-brand-smoke">
                    Aucune session {filter === "upcoming" ? "à venir" : filter === "past" ? "passée" : ""}.
                </div>
            ) : (
                <div className="mt-8 space-y-4">
                    {items.map((s) => (
                        <Link
                            key={s.id}
                            to={`/formateur/sessions/${s.id}`}
                            data-testid={`formateur-session-card-${s.id}`}
                            className="group flex flex-wrap items-center gap-6 rounded-2xl border border-brand-line bg-white p-6 shadow-premium transition hover:border-brand-orange"
                        >
                            <div className="flex-1 min-w-0">
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-brand-smoke">
                                    {s.cursus_slug}
                                </div>
                                <h3 className="mt-1 font-display text-2xl text-brand-ink">{s.formation_name}</h3>
                                <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-brand-smoke">
                                    <span className="inline-flex items-center gap-1.5">
                                        <Calendar className="h-3.5 w-3.5" /> {formatDate(s.start_date)}
                                    </span>
                                    <span className="inline-flex items-center gap-1.5">
                                        <MapPin className="h-3.5 w-3.5" /> {s.city} · {s.venue_name}
                                    </span>
                                    <span className="inline-flex items-center gap-1.5">
                                        <Users className="h-3.5 w-3.5" /> {s.enrollments_count}/{s.total_seats} stagiaires
                                    </span>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                {s.emargements_count > 0 && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-[10px] font-semibold text-green-700">
                                        <ClipboardCheck className="h-3 w-3" /> {s.emargements_count} émargement(s)
                                    </span>
                                )}
                                <ChevronRight className="h-4 w-4 text-brand-orange transition-transform group-hover:translate-x-1" />
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}
